import React, { useContext, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ErrorMessage } from "@hookform/error-message";
import {
  fetchData,
  USER_LIST,
  TERMS_AND_CONDITIONS,
  ENDPOINT,
} from "../../components/Service";
import { toastNotify } from "../../components/Helper";
import { Context } from "../../components/Context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import Cookies from "universal-cookie";
import configData from "../../config.json";
import moment from "moment";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import "./auth.scss";

const theme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary50: "#70bc1f",
    primary25: "#e8f3db",
    primary: "#70bc1f",
  },
});

const Registration = () => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  window.document.title = `${t("Registration")} | ${t("Panda")}`;

  async function submitRegistration(formdata) {
    try {
      setReload(true);
      setIsSubmitting(true); // Set the submitting state

      let user_info = {
        title: getValues("title") ?? "",
        firstname: getValues("firstname") ?? "",
        lastname: getValues("lastname") ?? "",
        email: getValues("email") ?? "",
        marketing_opt_email:
          getValues("marketing_opt_email").toString() == "true" ?? false,
        marketing_opt_mobile:
          getValues("marketing_opt_mobile").toString() == "true" ?? false,
        is_terms_accepted:
          context.is_terms_accepted ||
          getValues("is_terms_accepted")?.toString() == "true" ||
          false,
        birthdate: getValues("birthdate") ?? "",
        nationality: getValues("nationality") ?? "", // Include nationality in submission
        additional_info: {
          marital_status: getValues("marital_status") ?? "",
          household: {
            adults: getValues("adults") ? parseInt(getValues("adults")) : 0,
            children: getValues("children")
              ? parseInt(getValues("children"))
              : 0,
          },
        },
      };

      // Call the API to update user data
      fetchData(
        USER_LIST + "/" + context.id,
        "PATCH",
        { user: user_info },
        true,
        false,
        (res) => {
          if (res.status) {
            var userInfo = cookies.get("user");
            let userNewData = res.data.user;

            userNewData.key = userInfo && userInfo.key;
            userNewData.verify_id = userInfo && userInfo.verify_id;
            userNewData.is_loyalty_customer = context.is_loyalty_customer;
            userNewData.is_anonymous = context.is_anonymous;

            cookies.remove("user");

            // Update the user context
            setContext(userNewData);

            cookies.set("user", userNewData);

            toastNotify("success", t("Updated successfully"));

            // Navigate after the context has been updated
            setIsSubmitting(false); // Remove submitting state after API call
          } else {
            toastNotify(
              "danger",
              res.error?.message ?? t("Something went wrong")
            );
            setReload(false);
          }
        }
      );
    } catch (err) {
      toastNotify("danger", t("Invalid action perform"));
      setReload(false); // Stop the loader on error
      setIsSubmitting(false); // Reset the submission state
    }
  }

  const MOMENT_DATE_YMD = configData.formate.moment.ymd.date;
  const DATEPICKER_DATE = configData.formate.datepicker.dmy.date;

  const maritalStatus = [
    { value: "single", label: t("Single") },
    { value: "married", label: t("Married") },
    { value: "other", label: t("Other") },
  ];

  const noOfFamilyMember = [
    { value: "0", label: "00" },
    { value: "1", label: "01" },
    { value: "2", label: "02" },
    { value: "3", label: "03" },
    { value: "4", label: "04" },
    { value: "5", label: "05" },
  ];

  const [startDate, setStartDate] = useState(new Date());
  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm();
  const [context, setContext] = useContext(Context);
  const [reload, setReload] = useState(false);
  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedNationality, setSelectedNationality] = useState(null);
  const [nationalities, setNationalities] = useState([]);
  const [nationalityLoaded, setNationalityLoaded] = useState(false); // New state
  const [isSubmitting, setIsSubmitting] = useState(false); // State for managing submission

  const requiredFields = [
    "title",
    "firstname",
    "lastname",
    "email",
    "mobile",
    "birthdate",
    "nationality",
    "is_terms_accepted",
  ];

  const startDateHandler = (date) => {
    if (!moment(date, MOMENT_DATE_YMD, true).isValid()) {
      setError("birthdate", {
        type: "custom",
        message: t("Invalid Data Format"),
      });
    }
    setStartDate(date);
    setValue("birthdate", moment(date).format(MOMENT_DATE_YMD));
  };

  const handleProgress = () => {
    if (!nationalityLoaded) return; // Wait for nationality data to be loaded
    let totalFilledField = 0;

    for (const key of requiredFields) {
      if (getValues(key)) {
        totalFilledField++;
      } else if (
        !getValues(key) &&
        key === "is_terms_accepted" &&
        context.is_terms_accepted
      ) {
        totalFilledField++;
      }
    }

    const stepProgress = (
      (totalFilledField / requiredFields.length) *
      100
    ).toFixed();
    setProgress(stepProgress);
  };

  // Fetch nationality data after the component mounts
  useEffect(() => {
    async function fetchNationalityData() {
      try {
        const res = await fetch(
          `${ENDPOINT}/configs?group=general&key=nationality`,
          {
            headers: {
              "X-Refresh-Key": "",
              "X-Language": "en",
              "X-Panda-Source": "PandaClick",
              "X-PandaClick-Agent": "1",
            },
          }
        );
        const data = await res.json();
        if (data.status && data.data.configs.nationality) {
          const fetchedNationalities = data.data.configs.nationality.map(
            (nation) => ({
              value: nation.value,
              label: i18n.language === "ar" ? nation.name_ar : nation.name_en,
            })
          );
          setNationalities(fetchedNationalities);

          // After fetching nationalities, set the default nationality based on context
          if (context && context.nationality) {
            const initialNationality = fetchedNationalities.find(
              (n) => n.value === context.nationality
            );
            setSelectedNationality(initialNationality);
            setValue("nationality", initialNationality?.value || "");
          }
          setNationalityLoaded(true); // Mark nationality as loaded
        }
      } catch (error) {
        console.error("Failed to fetch nationality data", error);
      }
    }

    fetchNationalityData();
  }, [i18n.language, context]);

  const showTC = () => {
    fetchData(TERMS_AND_CONDITIONS, "GET", "", true, false, (res) => {
      if (res.status) {
        setTermsAndConditions(t("Panda plus terms"));
      } else {
        res.error &&
          res.error.message &&
          toastNotify(
            "danger",
            res.error.message ? res.error.message : t("Something went wrong")
          );
      }
      setReload(false);
    });
  };

  const acceptTC = () => {
    setValue("is_terms_accepted", true);
    setTermsAndConditions(false);
    handleProgress(); // Recalculate progress when terms are accepted
  };

  // Progress should be updated once the nationality is loaded
  useEffect(() => {
    if (nationalityLoaded) {
      handleProgress();
    }
  }, [nationalityLoaded]);

  useEffect(() => {
    setValue("firstname", context && context.firstname);
    setValue("lastname", context && context.lastname);
    setValue("mobile", context && context.mobile);
    setValue("email", context && context.email);
    setValue("title", context && context.title);
    setValue("marketing_opt_email", context && context.marketing_opt_email);
    setValue("marketing_opt_mobile", context && context.marketing_opt_mobile);
    setValue("gender", context && context.gender);
    setValue(
      "birthdate",
      context && context.birthdate
        ? context.birthdate
        : moment(startDate).format(MOMENT_DATE_YMD)
    );
    setValue(
      "address",
      context && context.additional_info && context.additional_info.address
    );
    setValue(
      "no_of_cities",
      context && context.additional_info && context.additional_info.city_id
    );
    setValue(
      "marital_status",
      context &&
        context.additional_info &&
        context.additional_info.marital_status
    );
    setValue(
      "adults",
      context &&
        context.additional_info &&
        context.additional_info &&
        context.additional_info.household &&
        context.additional_info.household.adults
    );
    setValue(
      "children",
      context &&
        context.additional_info &&
        context.additional_info &&
        context.additional_info.household &&
        context.additional_info.household.children
    );
    setValue(
      "store_location",
      context &&
        context.additional_info &&
        context.additional_info.store_location &&
        context.additional_info.store_location.length > 0 &&
        context.additional_info.store_location[0]
    );
    setValue(
      "category_preference",
      context && context.additional_info && context.additional_info.category_ids
    );
    setValue(
      "how_you_want_to_shop_online",
      context.additional_info.shop_preference
        ? context.additional_info.shop_preference.indexOf("online") > -1
          ? "online"
          : ""
        : ""
    );
    setValue(
      "how_you_want_to_shop_instore",
      context.additional_info.shop_preference
        ? context.additional_info.shop_preference.indexOf("in-store") > -1
          ? "in-store"
          : ""
        : ""
    );

    if (context && context.birthdate) {
      startDateHandler(new Date(context.birthdate));
    }
    handleProgress();
  }, [context]);

  useEffect(() => {
    const progressContainer = document.querySelector(".progress-container");
    const progressEl = progressContainer.querySelector(".progress");
    const percentageEl = progressContainer.querySelector(".percentage");

    progressEl.style.width = percentageEl.innerText = progress + "%";

    if (i18n.language === "ar") {
      percentageEl.style.right = progress + "%";
    } else {
      percentageEl.style.left = progress + "%";
    }
  }, [progress, i18n.language]);
  return (
    <>
      {context && (
        <section className="banner-form">
          <div className="container">
            <div className="row">
              <div className="col-lg-5 col-md-12 col-sm-12 p-0">
                <div className="panda-image left-box">
                  <img
                    src="images/bg-overlay.png"
                    className="bg-cover"
                    alt="bg-cover"
                  />
                  <div className="left-inner l">
                    <img src="/images/logo.png" alt="logo" />
                    {context && context.firstname && (
                      <h3 className="text-white mt-5">
                        {t("Hi")}, {context && context.firstname}
                      </h3>
                    )}
                    <h3 className="text-white mt-3">
                      {t("Welcome to Panda Plus")}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="col-lg-7 col-md-12 col-sm-12">
                <div className="row d-flex justify-content-center">
                  <div className="col-lg-8 col-md-12 col-sm-12">
                    <div className="main-form-section mt-4 mb-5 ">
                      <form
                        id="stepByStepForm"
                        className="form-stepper"
                        onSubmit={handleSubmit(submitRegistration)}
                        onChange={handleProgress}
                      >
                        <div className="col-md-12"></div>

                        <div className="col-md-12 mt-5">
                          <div className="progress-container">
                            <div
                              className={`progress ${
                                progress === 100 && "progress-100"
                              }`}
                            ></div>
                            <div className="percentage">0%</div>
                          </div>
                        </div>

                        <div className="main-form-panda ps-4 pe-4">
                          <div className="col-md-12">
                            <div className="form--header-container">
                              <h5 className="form--header-title pb-3 fw-bold py-3 mt-1">
                                {t("Let us get to know you")}
                              </h5>
                              <h6
                                style={{ fontSize: "0.7rem" }}
                                className="form-label text-green"
                              >
                                {t("(*) required")}
                              </h6>
                            </div>
                            <div className="mb-3">
                              <label className="form-label pt-1 mb-0 d-block">
                                {t("Title")}
                                <span>*</span>
                              </label>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  {...register("title", {
                                    required: `${t("Required field")}`,
                                  })}
                                  id="inlineradio1"
                                  value="mr"
                                  style={{ cursor: "pointer" }}
                                />
                                <label
                                  className="form-check-label text-muted"
                                  htmlFor="inlineradio1"
                                >
                                  {t("Mr.")}
                                </label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  {...register("title", {
                                    required: `${t("Required field")}`,
                                  })}
                                  id="inlineradio2"
                                  value="mrs"
                                  style={{ cursor: "pointer" }}
                                />
                                <label
                                  className="form-check-label text-muted"
                                  htmlFor="inlineradio2"
                                >
                                  {t("Mrs.")}
                                </label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  {...register("title", {
                                    required: `${t("Required field")}`,
                                  })}
                                  id="inlineradio2"
                                  value="miss"
                                  style={{ cursor: "pointer" }}
                                />
                                <label
                                  className="form-check-label text-muted"
                                  htmlFor="inlineradio2"
                                >
                                  {t("Miss.")}
                                </label>
                              </div>
                              <ErrorMessage
                                errors={errors}
                                name="title"
                                render={({ message }) => (
                                  <p className="error-message">{message}</p>
                                )}
                              />
                            </div>
                            <div className="mb-3">
                              <label htmlFor="firstname" className="form-label">
                                {t("First Name")}
                                <span>*</span>
                              </label>
                              <input
                                type="text"
                                id="firstname"
                                {...register("firstname", {
                                  required: `${t("Required field")}`,
                                })}
                                placeholder={t("First Name")}
                                maxLength="32"
                              />
                              <ErrorMessage
                                errors={errors}
                                name="firstname"
                                render={({ message }) => (
                                  <p className="error-message">{message}</p>
                                )}
                              />
                            </div>

                            <div className="mb-3">
                              <label htmlFor="lastname" className="form-label">
                                {t("Last Name")}
                                <span>*</span>
                              </label>
                              <input
                                type="text"
                                id="lastname"
                                {...register("lastname", {
                                  required: `${t("Required field")}`,
                                })}
                                placeholder={t("Last Name")}
                                maxLength="32"
                              />
                              <ErrorMessage
                                errors={errors}
                                name="lastname"
                                render={({ message }) => (
                                  <p className="error-message">{message}</p>
                                )}
                              />
                            </div>

                            <div className="mb-3">
                              <label htmlFor="mobile" className="form-label">
                                {t("Mobile Number")}
                                <span>*</span>
                              </label>
                              <div className="input-group numinput-wrap">
                                <span
                                  className="input-group-text"
                                  id="inputGroup-sizing-sm"
                                >
                                  <b>+966</b>
                                </span>
                                <input
                                  type="text"
                                  id="mobile"
                                  {...register("mobile", {
                                    required: `${t("Required field")}`,
                                    pattern: {
                                      value: /^5[0-9]{8}$/,
                                      message: `${t("Enter valid No")}`,
                                    },
                                  })}
                                  className="form-control text-dark "
                                  aria-describedby="inputGroup-sizing-sm"
                                  placeholder={t("Enter your no")}
                                  readOnly
                                />
                              </div>
                              <ErrorMessage
                                errors={errors}
                                name="mobile"
                                render={({ message }) => (
                                  <p className="error-message">{message}</p>
                                )}
                              />
                            </div>
                            <div className="mb-3">
                              <label htmlFor="email" className="form-label">
                                {t("Email")} <span>*</span>
                              </label>
                              <input
                                type="text"
                                id="email"
                                {...register("email", {
                                  required: `${t("Required field")}`,
                                  pattern: {
                                    value:
                                      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,})$/i,
                                    message: `${t("Email Invalid")}`,
                                  },
                                })}
                                placeholder={t("Email")}
                                maxLength="64"
                              />
                              <ErrorMessage
                                errors={errors}
                                name="email"
                                render={({ message }) => (
                                  <p className="error-message">{message}</p>
                                )}
                              />
                            </div>
                            <div className="mb-3">
                              <label htmlFor="birthdate" className="form-label">
                                {t("Birthday")}
                                <span>*</span>
                              </label>
                              <div className="input-group w-100">
                                <DatePicker
                                  selected={startDate}
                                  onChange={(date) => startDateHandler(date)}
                                  selectsStart
                                  dateFormat={DATEPICKER_DATE}
                                  maxDate={new Date()}
                                  className="form-control input-group-field"
                                  wrapperClassName="w-100"
                                  name="birthdate"
                                  showMonthDropdown
                                  showYearDropdown
                                  dropdownMode="select"
                                />
                                <FontAwesomeIcon
                                  icon={faCalendar}
                                  className="input-group-icon fs-14px"
                                />
                                <span className="sub-title w-100 mt-1">
                                  {t("Share your birthday")}
                                </span>
                                <ErrorMessage
                                  errors={errors}
                                  name="birthdate"
                                  render={({ message }) => (
                                    <p className="error-message">{message}</p>
                                  )}
                                />
                              </div>
                            </div>
                            {/* Nationality Select Field */}
                            <div className="mb-3">
                              <label className="form-label py-1">
                                {t("Nationality*")}
                              </label>
                              <Select
                                className="custom-react-select"
                                classNamePrefix="custom-react-select"
                                options={nationalities}
                                isSearchable={true}
                                id="nationality"
                                theme={theme}
                                value={selectedNationality}
                                required
                                placeholder={t("Select Nationality")}
                                onChange={(selected) => {
                                  setSelectedNationality(selected);
                                  setValue("nationality", selected.value, {
                                    shouldValidate: true,
                                  });
                                  handleProgress();
                                }}
                              />

                              <ErrorMessage
                                errors={errors}
                                name="nationality"
                                render={({ message }) => (
                                  <p className="error-message">{message}</p>
                                )}
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label py-1">
                                {t("Martial Status")}
                              </label>
                              <div className="input-group">
                                <Select
                                  defaultValue={
                                    context &&
                                    context.additional_info &&
                                    context.additional_info.marital_status
                                      ? maritalStatus[
                                          context.additional_info
                                            .marital_status === "single"
                                            ? 0
                                            : context.additional_info
                                                .marital_status === "married"
                                            ? 1
                                            : 2
                                        ]
                                      : ""
                                  }
                                  className="custom-react-select"
                                  classNamePrefix="custom-react-select"
                                  options={maritalStatus}
                                  isSearchable={false}
                                  id="marital_status"
                                  theme={theme}
                                  placeholder={t("Select")}
                                  onChange={(selected) => {
                                    setValue("marital_status", selected.value);
                                    handleProgress();
                                  }}
                                />
                              </div>
                            </div>
                            <div className="mb-3">
                              <label className="form-label py-1">
                                {t("Tell us how many people live with you?")}
                              </label>
                              <div className="input-group g-2">
                                <Select
                                  defaultValue={
                                    context &&
                                    context.additional_info &&
                                    context.additional_info.household &&
                                    context.additional_info.household.adults
                                      ? noOfFamilyMember[
                                          context.additional_info.household
                                            .adults
                                        ]
                                      : ""
                                  }
                                  className={`custom-react-select w-49 ${
                                    i18n.language === "ar" && "ms-auto"
                                  }`}
                                  classNamePrefix="custom-react-select"
                                  options={noOfFamilyMember}
                                  isSearchable={false}
                                  id="adults"
                                  theme={theme}
                                  placeholder={t("Adults")}
                                  onChange={(selected) => {
                                    setValue("adults", selected.value);
                                    handleProgress();
                                  }}
                                />

                                <Select
                                  defaultValue={
                                    context &&
                                    context.additional_info &&
                                    context.additional_info.household &&
                                    context.additional_info.household.children
                                      ? noOfFamilyMember[
                                          context.additional_info.household
                                            .children
                                        ]
                                      : ""
                                  }
                                  className={`custom-react-select w-49 ${
                                    i18n.language === "en" && "ms-auto"
                                  }`}
                                  classNamePrefix="custom-react-select"
                                  options={noOfFamilyMember}
                                  isSearchable={false}
                                  id="children"
                                  theme={theme}
                                  placeholder={t("Children")}
                                  onChange={(selected) => {
                                    setValue("children", selected.value);
                                    handleProgress();
                                  }}
                                />
                              </div>
                            </div>
                            <div className="checkbox row mb-3">
                              <label className="col-sm-12 form-label checkbox-content mb-1">
                                {t("Be the first to know about our best deals")}
                              </label>
                              <div className="check-bg">
                                <div className="form-check form-check-inline ">
                                  <input
                                    className="form-check-input"
                                    id="marketing_opt_email"
                                    type="checkbox"
                                    {...register("marketing_opt_email")}
                                    value="true"
                                  />
                                  <label
                                    className="form-check-label text-muted ms-2"
                                    htmlFor="marketing_opt_email"
                                  >
                                    {t("Yes to Email")}{" "}
                                  </label>
                                </div>
                                <div className="form-check form-check-inline">
                                  <input
                                    className="form-check-input"
                                    id="marketing_opt_mobile"
                                    type="checkbox"
                                    {...register("marketing_opt_mobile")}
                                    value="true"
                                  />
                                  <label
                                    className="form-check-label text-muted ms-2"
                                    htmlFor="marketing_opt_mobile"
                                  >
                                    {t("Yes to Mobile")}
                                  </label>
                                </div>
                                {(!context.is_loyalty_customer ||
                                  context.is_anonymous) && (
                                  <div
                                    className="form-check form-check-inline mt-2"
                                    onClick={showTC}
                                  >
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      {...register("is_terms_accepted", {
                                        required: `${t("Required field")}`,
                                      })}
                                      id="is_terms_accepted"
                                      value="true"
                                    />
                                    <label className="form-check-label text-muted ms-2">
                                      {t("Please accept")}{" "}
                                      <span className="brn-link text-primary">
                                        {t("Term & Condition")}
                                      </span>
                                    </label>
                                    <ErrorMessage
                                      errors={errors}
                                      name="is_terms_accepted"
                                      render={({ message }) => (
                                        <p className="error-message">
                                          {message}
                                        </p>
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 ">
                            <div className="submit-netx-p">
                              <button
                                type="submit"
                                className="button-grey btn-hover"
                              >
                                {reload && (
                                  <span className="spinner-border spinner-border-sm mr-1"></span>
                                )}{" "}
                                {t("Submit")}
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                    {termsAndConditions && (
                      <>
                        <div
                          className="modal fade show"
                          id="termsConditionModal"
                          tabindex="-1"
                          role="dialog"
                          aria-labelledby="termsConditionLabel"
                          aria-hidden="true"
                          style={{ display: "block", paddingRight: "15px" }}
                        >
                          <div
                            className="modal-dialog modal-lg"
                            role="document"
                          >
                            <div className="modal-content border-green-light">
                              <div className="modal-header">
                                <h5
                                  className="modal-title text-green"
                                  id="termsConditionLabel"
                                >
                                  {t("Term & Condition")}
                                </h5>
                              </div>
                              <div
                                className="modal-body text-muted"
                                dangerouslySetInnerHTML={{
                                  __html: termsAndConditions,
                                }}
                              ></div>
                              <div className="modal-footer main-form-section">
                                <button
                                  type="button"
                                  className="button-grey btn-hover w-25"
                                  onClick={acceptTC}
                                >
                                  {t("Accept")}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="modal-backdrop fade show"></div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Registration;
